// The usage meter in the sidebar's foot (D47, D107; plan step 5.1; Console D's nav-meter): on the
// trial (14 days of Pro), the days left and its 50k captured sessions; on a plan, the period's sessions against
// its cap or included sessions. The count comes from GET /orgs/{orgId}/usage; until it answers
// (or if it cannot), the trial meter tracks time and states the cap. It links to the plan.
import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import type { OrgMembership, Usage } from '../api/types'
import { useI18n } from '../i18n'

export const PLAN_KEY = {
  free: 'plan.free',
  trial: 'plan.trial',
  pro: 'plan.pro',
  enterprise: 'plan.enterprise',
} as const

export const TRIAL_DAYS = 14
export const TRIAL_SESSIONS = 50_000
const DAY = 86_400_000

export function trialState(
  org: Pick<OrgMembership, 'plan' | 'status' | 'trial_ends_at'>,
  now = Date.now(),
) {
  if (org.plan !== 'trial' || !org.trial_ends_at) return undefined
  const left = Math.max(0, Math.ceil((new Date(org.trial_ends_at).getTime() - now) / DAY))
  // Ended, until the next metering run moves the organization to Free (D107).
  const ended = left === 0
  return { left, ended, used: Math.min(1, (TRIAL_DAYS - left) / TRIAL_DAYS) }
}

/** The sessions meter: how much of the cap (or the included sessions) this period used. */
export function sessionsState(usage: Usage | undefined) {
  if (!usage) return undefined
  const limit =
    usage.cap?.sessions ?? (usage.included_sessions > 0 ? usage.included_sessions : null)
  return {
    used: usage.captured_sessions,
    limit,
    share: limit ? Math.min(1, usage.captured_sessions / limit) : null,
    capped: usage.capped,
  }
}

export function TrialMeter({
  org,
  usage,
  projectId,
}: {
  org: OrgMembership
  usage?: Usage | undefined
  /** The project the plan settings open in; without one the meter is not a link. */
  projectId?: string
}) {
  const { t, number } = useI18n()
  const trial = trialState(org)
  const sessions = sessionsState(usage)
  const compact = (n: number) => number(n, { notation: 'compact' })
  // The meter's short count reads like the mock's ("14.2k / 25k"); the spoken one stays as is.
  const short = (n: number) => compact(n).toLowerCase()
  // The count, short to fit beside the days; its words are for screen readers.
  const count: ReactNode = sessions ? (
    <>
      <span className="mono" aria-hidden="true">
        {sessions.limit
          ? `${short(sessions.used)} / ${short(sessions.limit)}`
          : short(sessions.used)}
      </span>
      <span className="vh">
        {sessions.limit
          ? t('usage.of', { used: compact(sessions.used), limit: compact(sessions.limit) })
          : t('usage.thisPeriod', { used: compact(sessions.used) })}
      </span>
    </>
  ) : trial ? (
    <>
      <span className="mono" aria-hidden="true">
        {`≤ ${short(TRIAL_SESSIONS)}`}
      </span>
      <span className="vh">{t('trial.cap', { sessions: compact(TRIAL_SESSIONS) })}</span>
    </>
  ) : null
  const share = sessions?.share ?? (sessions ? null : (trial?.used ?? null))
  const body = (
    <>
      <span className="r">
        {trial ? (
          <span>
            {t('trial.title')} ·{' '}
            <b>{trial.ended ? t('trial.ended') : t('trial.daysLeft', { days: trial.left })}</b>
          </span>
        ) : (
          <span>
            <b>{t(PLAN_KEY[org.plan])}</b> · {org.name}
          </span>
        )}
        <span>{count}</span>
      </span>
      {share === null ? null : (
        <span className={share > 0.7 ? 'meter-bar warn' : 'meter-bar'} aria-hidden="true">
          <i style={{ width: `${Math.max(2, share * 100)}%` }} />
        </span>
      )}
      {sessions?.capped ? (
        <span className="mono c-warn" role="status">
          {t('usage.capped')}
        </span>
      ) : null}
    </>
  )
  return projectId ? (
    <Link
      className="nav-meter"
      to="/o/$orgId/p/$projectId/settings/$section"
      params={{ orgId: org.id, projectId, section: 'plan' }}
      title={trial ? t('trial.title') : t(PLAN_KEY[org.plan])}
    >
      {body}
    </Link>
  ) : (
    <div className="nav-meter">{body}</div>
  )
}
